import React, { useEffect, useMemo, useState } from "react";
import { Footer, Navbar } from "../components";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { formatRupiah } from "../utils/currency";
import api from "../utils/api";
import { clearCart } from "../redux/action";

const Checkout = () => {
  const items = useSelector((state) => state.handleCart);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isProcessing, setIsProcessing] = useState(false);

  // Load Midtrans Snap Script
  useEffect(() => {
    // Only load once
    if (document.getElementById('midtrans-script')) {
      return;
    }
    const script = document.createElement('script');
    // using sandbox link for testing. Production should use: https://app.midtrans.com/snap/snap.js
    script.src = 'https://app.sandbox.midtrans.com/snap/snap.js';
    script.id = 'midtrans-script';
    // The precise attribute isn't strictly required if we pass the snapToken to window.snap.pay
    // but it avoids warnings if we have one. We can leave it out.
    document.body.appendChild(script);

    return () => {
      // Optional: Cleanup script on unmount
      const el = document.getElementById('midtrans-script');
      if (el) el.remove();
    };
  }, []);

  const { subtotal, totalItems, total, adminFee } = useMemo(() => {
    const adminFeeCalc = 4000; // As specified by backend setting default
    const subtotalCalc = (items || []).reduce(
      (acc, it) => acc + (Number(it.final_price || it.price) || 0) * (Number(it.qty) || 0),
      0
    );
    const totalItemsCalc = (items || []).reduce((acc, it) => acc + (Number(it.qty) || 0), 0);
    const totalCalc = subtotalCalc + (items?.length ? adminFeeCalc : 0);

    return { subtotal: subtotalCalc, totalItems: totalItemsCalc, total: totalCalc, adminFee: adminFeeCalc };
  }, [items]);

  const handleCheckout = async (paymentMethod) => {
    if (!isAuthenticated) {
      toast.error("Silakan login terlebih dahulu untuk checkout.");
      navigate("/login", { state: { from: "/checkout" } });
      return;
    }

    if (items.length === 0) return;

    setIsProcessing(true);
    let orderId = null;

    try {
      // 1. Create Order
      const payload = {
        items: items.map(it => ({ product_id: it.id, quantity: it.qty }))
      };
      const orderRes = await api.post('/api/orders', payload);
      orderId = orderRes.data.order_id;

      // 2. Process Payment
      if (paymentMethod === 'wallet') {
        await api.post(`/api/payment/${orderId}/wallet`);
        toast.success("Pesanan berhasil dibayar menggunakan Saldo Wallet!");
        dispatch(clearCart());
        navigate('/orders');
      } else if (paymentMethod === 'midtrans') {
        const snapRes = await api.post(`/api/payment/${orderId}/initiate`);
        const snapToken = snapRes.data.snap_token;
        
        if (window.snap) {
          window.snap.pay(snapToken, {
            onSuccess: function (result) {
              toast.success("Pembayaran berhasil!");
              dispatch(clearCart());
              navigate('/orders');
            },
            onPending: function (result) {
              toast.success("Menunggu pembayaran diselesaikan.");
              dispatch(clearCart());
              navigate('/orders');
            },
            onError: function (result) {
              toast.error("Pembayaran gagal!");
            },
            onClose: function () {
              toast.error("Anda menutup jendela pembayaran. Pesanan Anda berstatus Menunggu Pembayaran.");
              dispatch(clearCart());
              navigate('/orders');
            }
          });
        } else {
          toast.error("Gagal memuat gateway pembayaran. Silakan cek pesanan di halaman Pesanan.");
          dispatch(clearCart());
          navigate('/orders');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Gagal memproses pesanan.");
      // If order was created but payment failed, still clear cart and go to orders
      if (orderId) {
         dispatch(clearCart());
         navigate('/orders');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const EmptyCart = () => (
    <section className="bg-zinc-950 text-white min-h-[60vh] flex items-center">
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8 w-full">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center ring-1 ring-white/5">
          <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
             <i className="fa fa-shopping-cart text-3xl text-zinc-500"></i>
          </div>
          <h1 className="text-2xl font-extrabold sm:text-3xl">Keranjang Kosong</h1>
          <p className="mt-3 text-zinc-400">Anda belum menambahkan produk apapun ke dalam keranjang.</p>
          <Link
            to="/product"
            className="mt-8 inline-flex items-center justify-center rounded-xl bg-emerald-500 px-6 py-3.5 text-sm font-extrabold text-white transition hover:bg-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_25px_rgba(16,185,129,0.4)]"
          >
            ← Mulai Belanja
          </Link>
        </div>
      </div>
    </section>
  );

  const ShowCheckout = () => (
    <main className="bg-zinc-950 text-white min-h-screen py-8">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between pb-6 border-b border-white/10">
          <div>
            <h1 className="text-2xl font-extrabold sm:text-3xl">Checkout</h1>
            <p className="mt-1 text-sm text-zinc-400">
              Periksa ringkasan pesanan Anda sebelum melakukan pembayaran.
            </p>
          </div>
          <Link to="/cart" className="hidden sm:flex text-sm font-semibold text-zinc-300 hover:text-white items-center gap-2">
            <i className="fa fa-arrow-left"></i> Kembali ke Cart
          </Link>
        </div>

        <div className="mt-8 grid gap-8 md:grid-cols-5 items-start">
          {/* Items List */}
          <section className="md:col-span-3">
            <h2 className="text-lg font-bold mb-4">Detail Produk</h2>
            <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
               <ul className="divide-y divide-white/5">
                  {items.map((it) => (
                    <li key={it.id} className="p-4 sm:p-6 flex gap-4">
                       <div className="w-20 h-20 rounded-xl bg-zinc-900 overflow-hidden shrink-0 border border-white/5">
                          {it.image ? (
                             <img src={it.image} alt={it.title} className="w-full h-full object-cover" />
                          ) : (
                             <div className="w-full h-full flex items-center justify-center text-zinc-700">
                               <i className="fa fa-image text-xl"></i>
                             </div>
                          )}
                       </div>
                       <div className="flex-1 flex flex-col">
                          <h3 className="text-base font-bold text-white line-clamp-2">{it.title}</h3>
                          <p className="text-xs text-zinc-400 mt-1">{it.product_type === 'digital' ? 'Produk Digital (Instant Download)' : 'Produk Fisik'}</p>
                          <div className="mt-auto flex items-center justify-between">
                             <span className="text-sm font-semibold text-zinc-300">{it.qty} x {formatRupiah(it.final_price || it.price)}</span>
                             <span className="font-extrabold text-emerald-400">{formatRupiah((Number(it.final_price || it.price)) * Number(it.qty))}</span>
                          </div>
                       </div>
                    </li>
                  ))}
               </ul>
            </div>
          </section>

          {/* Order summary */}
          <aside className="md:col-span-2 sticky top-24">
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6 shadow-[0_0_30px_rgba(16,185,129,0.05)] backdrop-blur-xl">
              <h2 className="text-lg font-extrabold text-white">Ringkasan Pesanan</h2>

              <div className="mt-6 space-y-4 text-sm">
                <div className="flex items-center justify-between text-zinc-300">
                  <span>Total Harga ({totalItems} barang)</span>
                  <span className="font-semibold text-white">{formatRupiah(subtotal)}</span>
                </div>

                <div className="flex items-center justify-between text-zinc-300">
                  <span>Biaya Admin</span>
                  <span className="font-semibold text-white">{formatRupiah(adminFee)}</span>
                </div>

                <div className="h-px w-full bg-white/10 my-4" />

                <div className="flex items-center justify-between">
                  <span className="text-base font-extrabold text-white">Total Tagihan</span>
                  <span className="text-xl font-extrabold text-emerald-400">{formatRupiah(total)}</span>
                </div>
              </div>

              <div className="mt-8 space-y-3">
                 <button
                    onClick={() => handleCheckout('wallet')}
                    disabled={isProcessing}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-500 px-4 py-4 text-sm font-extrabold text-white transition hover:bg-indigo-400 hover:shadow-[0_0_20px_rgba(99,102,241,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                    {isProcessing ? (
                       <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                       <><i className="fa fa-wallet"></i> Bayar dengan Saldo Wallet</>
                    )}
                 </button>
                 
                 <button
                    onClick={() => handleCheckout('midtrans')}
                    disabled={isProcessing}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-4 text-sm font-extrabold text-white transition hover:bg-emerald-400 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                    {isProcessing ? (
                       <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                       <><i className="fa fa-credit-card"></i> Metode Pembayaran Lainnya</>
                    )}
                 </button>
              </div>
              
              <p className="mt-5 text-center text-xs text-zinc-500 flex items-center justify-center gap-2">
                <i className="fa fa-shield"></i> Pembayaran Aman Terenkripsi
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );

  return (
    <>
      <Navbar />
      {items?.length ? <ShowCheckout /> : <EmptyCart />}
      <Footer />
    </>
  );
};

export default Checkout;
